/**
 * Local registry of the channels this device belongs to, so a user can keep
 * multiple clipboard channels/groups and re-open them. Persisted in IndexedDB.
 *
 * NOTE: the effective pairing secret (S) is stored so the device can re-derive
 * channel keys and verify members on reconnect — the device is already a member,
 * so this is equivalent to holding its own session credential.
 */
import { STORE_CHANNELS, idbDelete, idbGet, idbPut } from "./crypto/idb";
import type { ChannelType } from "@zeroclipboard/shared";
import type { ReplayState } from "./engine";

const INDEX_KEY = "__index__";

export interface SavedChannel {
  channelId: string;
  type: ChannelType;
  /** Effective pairing secret S, base64url. */
  secretB64: string;
  /** True when this device created the channel (creator authority). */
  creator: boolean;
  label: string;
  createdAt: number;
  lastOpenedAt: number;
}

async function readIndex(): Promise<string[]> {
  return (await idbGet<string[]>(STORE_CHANNELS, INDEX_KEY)) ?? [];
}

async function writeIndex(ids: string[]): Promise<void> {
  await idbPut(STORE_CHANNELS, INDEX_KEY, ids);
}

export async function saveChannel(ch: SavedChannel): Promise<void> {
  await idbPut(STORE_CHANNELS, ch.channelId, ch);
  const idx = await readIndex();
  if (!idx.includes(ch.channelId)) {
    idx.push(ch.channelId);
    await writeIndex(idx);
  }
}

export async function getChannel(
  channelId: string
): Promise<SavedChannel | undefined> {
  if (channelId === INDEX_KEY) return undefined;
  return idbGet<SavedChannel>(STORE_CHANNELS, channelId);
}

export async function listChannels(): Promise<SavedChannel[]> {
  const idx = await readIndex();
  const out: SavedChannel[] = [];
  for (const id of idx) {
    const ch = await idbGet<SavedChannel>(STORE_CHANNELS, id);
    if (ch) out.push(ch);
  }
  return out.sort((a, b) => b.lastOpenedAt - a.lastOpenedAt);
}

export async function removeChannel(channelId: string): Promise<void> {
  await idbDelete(STORE_CHANNELS, channelId);
  await idbDelete(STORE_CHANNELS, `replay:${channelId}`);
  await writeIndex((await readIndex()).filter((x) => x !== channelId));
}

export async function touchChannel(channelId: string): Promise<void> {
  const ch = await getChannel(channelId);
  if (ch) {
    ch.lastOpenedAt = Date.now();
    await idbPut(STORE_CHANNELS, channelId, ch);
  }
}

/** Persisted per-device replay state for a channel (spec §16). */
export async function getReplayState(channelId: string): Promise<ReplayState> {
  return (
    (await idbGet<ReplayState>(STORE_CHANNELS, `replay:${channelId}`)) ?? {
      sendSeq: 0,
      seen: {},
    }
  );
}

export async function saveReplayState(
  channelId: string,
  state: ReplayState
): Promise<void> {
  await idbPut(STORE_CHANNELS, `replay:${channelId}`, state);
}
